import React from "react";
import {
    ApartmentOutlined,
    ArrowLeftOutlined,
    LogoutOutlined,
    MailOutlined,
    SettingOutlined,
    UserOutlined,
    UserSwitchOutlined,
} from "@ant-design/icons";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import AppShell, {
    NavPopoverAction,
    bottomNavGroup,
    navSection,
    useNavPopovers,
} from "../../layout/AppShell";
import { useUserState } from "../../../common/UserContext";
import { stopImpersonating } from "../../../common/api/AdminDashboardClient";
import { geturl } from "../../../common/api/SafeClient";
import { usePageTracking } from "../../../common/GoogleAnalytics/ReactRouter";

// Internal operations dashboard for the Infinity Loop team.
export const DashboardApp: React.FC = () => {
    usePageTracking();

    const navigate = useNavigate();
    const { pathname } = useLocation();
    // The nav entry of the page shown: the first path segment under the dashboard.
    const selectedKey = pathname.split("/")[3] || "organizations";

    const { me } = useUserState();
    const { popoverNavItem, closePopovers } = useNavPopovers();

    const goTo = (path: string) => {
        closePopovers();
        navigate(path);
    };

    const fullName = [me.firstName, me.lastName].filter(Boolean).join(" ") || me.name;
    // While impersonating, the entry names the org being acted as, not the bot user.
    const impersonatedOrg =
        me.impersonating?.organizationName || me.impersonating?.organizationDomain;

    const exitImpersonation = async () => {
        try {
            await stopImpersonating();
        } finally {
            // A full navigation, not a state update: the session user just
            // changed, so every piece of loaded state is stale.
            window.location.assign("/superadmin/dashboard");
        }
    };

    const menuItems = [
        navSection("management", "Management", [
            {
                key: "organizations",
                icon: <ApartmentOutlined />,
                label: "Organizations",
                onClick: () => navigate("/superadmin/dashboard/organizations"),
            },
            {
                key: "notifications",
                icon: <MailOutlined />,
                label: "Notifications",
                onClick: () => navigate("/superadmin/dashboard/notifications"),
            },
        ]),
        bottomNavGroup("account", [
            {
                key: "back-to-oasis",
                icon: <ArrowLeftOutlined />,
                label: "Back to Oasis",
                onClick: () => navigate("/"),
            },
            popoverNavItem(
                "profile",
                me.impersonating ? <UserSwitchOutlined /> : <UserOutlined />,
                me.impersonating ? `Bot: ${impersonatedOrg}` : fullName,
                <>
                    <NavPopoverAction
                        icon={<SettingOutlined />}
                        onClick={() => goTo("/preferences")}
                    >
                        My Account
                    </NavPopoverAction>
                    {me.impersonating && (
                        <NavPopoverAction icon={<UserSwitchOutlined />} onClick={exitImpersonation}>
                            Exit impersonation
                        </NavPopoverAction>
                    )}
                    <NavPopoverAction
                        icon={<LogoutOutlined />}
                        // A full navigation rather than a route change: the API
                        // clears the session and redirects to the login page.
                        onClick={() => window.location.assign(geturl("/api/logout"))}
                    >
                        Log out
                    </NavPopoverAction>
                </>
            ),
        ]),
    ];

    return (
        <AppShell
            logo={
                <Link to="/superadmin/dashboard">
                    <img src="/infinity-loop-logo-nav.svg" alt="Infinity Loop admin dashboard" />
                </Link>
            }
            compactLogo={
                <Link to="/superadmin/dashboard">
                    <img
                        src="/infinity-loop-logo-nav-compact.svg"
                        alt="Infinity Loop admin dashboard"
                    />
                </Link>
            }
            menuItems={menuItems}
            selectedKeys={[selectedKey]}
            defaultOpenKeys={["management"]}
        >
            <Outlet />
        </AppShell>
    );
};
